import { Icon } from '@customerio/pluma-components/react';
import type { SearchResult } from '@/services/auth/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { getActionQueryOptions } from '@/services/db/action/queries';
import { getActionIcon, getIconNameFromSearchResult } from './utils/result-icons';

type SearchResultIconProps = { item: SearchResult };
type PushActionIconProps = { workspaceId: number; actionId: string };

function PushActionIcon({ workspaceId, actionId }: PushActionIconProps) {
  // The search index omits send_to_platform. Resolve only push actions, sharing
  // the normal action cache rather than guessing from their editable names.
  const queryClient = useQueryClient();
  const { data } = useQuery({
    ...getActionQueryOptions(workspaceId, actionId, queryClient),
    enabled: workspaceId > 0 && actionId !== '',
    // Campaign-list sideloads seed sparse action rows without the platform.
    // Treat those as stale even when their cache timestamp is fresh.
    staleTime: (query) => (query.state.data?.send_to_platform === undefined ? 0 : 60_000),
    retry: false,
  });
  // Until the platform resolves (or if the lookup fails) show the native push glyph.
  return <Icon name={getActionIcon('push_action', data?.send_to_platform)} size="sm" color="primary" />;
}

export function SearchResultIcon({ item }: SearchResultIconProps) {
  const hasWebPush = useFeatureFlag(AccountFeatureFlag.WebPush);
  if (hasWebPush && item.kind === 'action' && item.other.action_type === 'push_action') {
    return <PushActionIcon workspaceId={Number(item.other.env_id)} actionId={item.other.id} />;
  }
  if (hasWebPush && item.kind === 'template' && item.other.template_type === 'push' && item.other.action_id != null) {
    return <PushActionIcon workspaceId={Number(item.other.env_id)} actionId={item.other.action_id} />;
  }
  return <Icon name={getIconNameFromSearchResult(item)} size="sm" color="primary" />;
}
